/**
 * Offboarding Acknowledgment Modal (Step 1)
 * Collects agreement to terms, electronic signature, and personal email.
 * Continue button passes data to parent for survey step.
 */
import Button from 'tailwind-components/Button/Button';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Checkbox, FormControlLabel, TextField, Tooltip } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { getAgreementText, isEmailValid } from './helpers';

const EMAIL_INFO_TOOLTIP =
  'The email you enter will be used to sign documents. Please use correct and monitored email addresses.';

export default function OffboardAcknowledgementModal({
  formData,
  setFormData,
  source
}) {
  const [signatureCanvasSize, setSignatureCanvasSize] = useState({
    width: 500,
    height: 150
  });
  const [isResizing, setIsResizing] = useState(false);

  const signatureRef = useRef(null);
  const signatureContainerRef = useRef(null);
  const restoringRef = useRef(false); // Prevenir múltiples restauraciones simultáneas

  // Resize canvas responsivo
  useEffect(() => {
    if (!signatureContainerRef.current) return;

    const container = signatureContainerRef.current;
    const updateSize = () => {
      if (
        container?.clientWidth &&
        container.clientWidth !== signatureCanvasSize.width
      ) {
        setIsResizing(true);

        // Guardar firma actual antes de resize
        const currentSignature =
          signatureRef.current && !signatureRef.current.isEmpty()
            ? signatureRef.current.toDataURL()
            : formData.signature?.dataURL;

        setSignatureCanvasSize({
          width: container.clientWidth,
          height: 150
        });

        // Restaurar firma después del resize
        if (currentSignature && !formData.signature?.isEmpty) {
          setTimeout(() => {
            if (signatureRef.current && !restoringRef.current) {
              restoringRef.current = true;
              signatureRef.current.clear();
              signatureRef.current.fromDataURL(currentSignature, { ratio: 1 });
              restoringRef.current = false;
              setIsResizing(false);
            }
          }, 50);
        } else {
          setIsResizing(false);
        }
      }
    };

    updateSize();
    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, []);

  // Restaurar firma al volver (Back button) - UN SOLO useEffect
  useEffect(() => {
    if (
      !formData.signature?.dataURL ||
      formData.signature?.isEmpty ||
      isResizing
    )
      return;

    const dataURL = formData.signature.dataURL;

    const restoreSignature = () => {
      if (signatureRef.current && !restoringRef.current) {
        // Verificar si el canvas está vacío antes de restaurar
        if (signatureRef.current.isEmpty()) {
          restoringRef.current = true;
          signatureRef.current.clear(); // Limpiar antes de restaurar
          signatureRef.current.fromDataURL(dataURL, { ratio: 1 });
          restoringRef.current = false;
        }
      }
    };

    // Esperar a que el canvas esté listo
    const timer = setTimeout(restoreSignature, 100);

    return () => clearTimeout(timer);
  }, [formData.signature?.dataURL, formData.signature?.isEmpty, isResizing]);

  const handleSignatureClear = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
      setFormData({ ...formData, signature: { dataURL: '', isEmpty: true } });
    }
  };

  const handleSignatureEnd = () => {
    if (restoringRef.current || isResizing) return; // No actualizar durante restauración o resize

    if (signatureRef.current) {
      const isEmpty = signatureRef.current.isEmpty();
      setFormData({
        ...formData,
        signature: {
          dataURL: isEmpty ? '' : signatureRef.current.toDataURL(),
          isEmpty
        }
      });
    }
  };

  return (
    <>
      {getAgreementText(source)}

      <FormControlLabel
        control={
          <Checkbox
            checked={formData.hasAgreed}
            onChange={e =>
              setFormData({ ...formData, hasAgreed: e.target.checked })
            }
          />
        }
        label='I Agree'
        sx={{ marginTop: 2, marginBottom: 2, display: 'block' }}
      />

      <p>
        <b>Signature:</b>
      </p>
      <div
        ref={signatureContainerRef}
        style={{
          border: '1px solid #ccc',
          borderRadius: 4,
          marginBottom: 8,
          overflow: 'hidden',
          width: '100%'
        }}
      >
        <SignatureCanvas
          ref={signatureRef}
          penColor='black'
          canvasProps={{
            width: signatureCanvasSize.width,
            height: signatureCanvasSize.height,
            className: 'signature-canvas'
          }}
          backgroundColor='#ffffff'
          onEnd={handleSignatureEnd}
        />
      </div>
      <Button
        text='Clear Signature'
        variant='secondary'
        size='small'
        onClick={handleSignatureClear}
      />

      <p>
        <b>
          {`Please provide ${
            source === 'agencyTable' ? "the agent's" : 'your'
          } current personal email address:`}
          <Tooltip title={EMAIL_INFO_TOOLTIP} placement='top'>
            <InfoOutlined
              sx={{
                fontSize: 18,
                verticalAlign: 'middle',
                marginLeft: 0.5,
                cursor: 'help'
              }}
            />
          </Tooltip>
        </b>
      </p>
      <TextField
        fullWidth
        variant='outlined'
        type='email'
        placeholder='email@example.com'
        value={formData.email}
        onChange={e =>
          setFormData({ ...formData, email: e.target.value.trim() })
        }
        error={!!formData.email.trim() && !isEmailValid(formData.email)}
        helperText={
          formData.email.trim() && !isEmailValid(formData.email)
            ? 'Please enter a valid email address'
            : ''
        }
        sx={{ marginTop: 0.5, marginBottom: 0.5 }}
      />
      {source === 'agentInfo' && (
        <>
          <p>
            <b>
              Please provide new employer/agency email:
              <Tooltip title={EMAIL_INFO_TOOLTIP} placement='top'>
                <InfoOutlined
                  sx={{
                    fontSize: 18,
                    verticalAlign: 'middle',
                    marginLeft: 0.5,
                    cursor: 'help'
                  }}
                />
              </Tooltip>
            </b>
          </p>
          <TextField
            fullWidth
            variant='outlined'
            type='email'
            placeholder='employer-or-agency@example.com'
            value={formData.newEmployer ?? ''}
            onChange={e =>
              setFormData({ ...formData, newEmployer: e.target.value.trim() })
            }
            error={
              !!(formData.newEmployer ?? '').trim() &&
              !isEmailValid(formData.newEmployer ?? '')
            }
            helperText={
              (formData.newEmployer ?? '').trim() &&
              !isEmailValid(formData.newEmployer ?? '')
                ? 'Please enter a valid email address'
                : ''
            }
            sx={{ marginTop: 0.5, marginBottom: 0.5 }}
          />
        </>
      )}
      <p>
        <b>
          {`Please note that the ${
            source === 'agencyTable' ? "the agent's email" : 'email'
          } on file will be updated to ${
            source === 'agencyTable' ? "the agent's" : 'your'
          } current
          personal email address and ${
            source === 'agencyTable' ? "the agent's" : 'your'
          } O\'Neill Agent portal login will
          be updated to reflect this email.`}
        </b>
      </p>
      <p>
        <b>
          If you have any questions about Offboarding, please reach out to our
          Support team:{' '}
          <span style={{ color: '#3682ac', fontWeight: 'bold' }}>
            support@oneillmarketing.net
          </span>{' '}
          or 813-489-4156.
        </b>
      </p>
    </>
  );
}

OffboardAcknowledgementModal.propTypes = {
  setFormData: PropTypes.func.isRequired,
  formData: PropTypes.shape({
    hasAgreed: PropTypes.bool,
    email: PropTypes.string,
    newEmployer: PropTypes.string,
    signature: PropTypes.shape({
      isEmpty: PropTypes.bool,
      dataURL: PropTypes.string
    })
  }),
  source: PropTypes.string
};
